import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";

const KEY = "ddi_consent";

type W = Window & { gtag?: (...args: unknown[]) => void; dataLayer?: unknown[] };

function applyConsent(granted: boolean) {
  const w = window as W;
  const v = granted ? "granted" : "denied";
  w.gtag?.("consent", "update", {
    ad_storage: v,
    analytics_storage: v,
    ad_user_data: v,
    ad_personalization: v,
  });
  w.dataLayer?.push({ event: granted ? "consent_granted" : "consent_denied" });
}

export function CookieBanner() {
  // null = not decided yet (blocking modal); false = reopened via settings (non-blocking bar)
  const [mode, setMode] = useState<null | "blocking" | "settings">(null);
  const { pathname } = useLocation();
  const isEn = pathname.startsWith("/en");

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setMode("blocking");
    } catch {
      setMode("blocking");
    }
    const reopen = () => setMode("settings");
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  // Lock page scroll while the visitor has not made a choice yet.
  useEffect(() => {
    if (mode !== "blocking" || pathname.startsWith("/admin")) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mode, pathname]);

  if (mode === null || pathname.startsWith("/admin")) return null;

  const choose = (granted: boolean) => {
    try {
      localStorage.setItem(KEY, granted ? "granted" : "denied");
    } catch {
      /* ignore */
    }
    applyConsent(granted);
    setMode(null);
  };

  const dialog = (
    <div
      role="dialog"
      aria-modal={mode === "blocking" ? true : undefined}
      aria-label={isEn ? "Cookie preferences" : "Cookievoorkeuren"}
      className={
        mode === "blocking"
          ? "relative z-[101] mx-auto w-[calc(100%-2rem)] max-w-lg rounded-xl border border-border bg-card p-6 text-card-foreground shadow-2xl"
          : "relative z-[101] mx-auto max-w-3xl rounded-xl border border-border bg-card/95 p-4 text-card-foreground shadow-lg backdrop-blur md:p-5"
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <p className={mode === "blocking" ? "text-sm text-foreground" : "text-sm text-muted-foreground"}>
            {isEn
              ? "We use cookies to measure site usage and improve our ads. Only with your consent will we use analytics and advertising cookies."
              : "Wij gebruiken cookies om het gebruik van de site te meten en onze advertenties te verbeteren. Alleen met jouw toestemming plaatsen we analytische en advertentiecookies."}{" "}
            <Link to={isEn ? "/en/privacy" : "/privacy"} className="underline hover:text-foreground">
              {isEn ? "Privacy policy" : "Privacybeleid"}
            </Link>
          </p>
        </div>
        <div className="flex w-full gap-2">
          <Button variant="outline" className="flex-1" onClick={() => choose(false)}>
            {isEn ? "Decline" : "Weigeren"}
          </Button>
          <Button className="flex-1" onClick={() => choose(true)}>
            {isEn ? "Accept" : "Akkoord"}
          </Button>
        </div>
      </div>
    </div>
  );

  if (mode === "blocking") {
    return (
      <div
        data-testid="consent-overlay"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-0 backdrop-blur-sm"
      >
        {dialog}
      </div>
    );
  }

  return <div className="fixed inset-x-3 bottom-3 z-[100]">{dialog}</div>;
}

export default CookieBanner;
